<template>
  <UiBreadcrumb
    v-if="isLoading || breadcrumbItems.length > 1"
    :class="containerClass"
    :items="items"
    :is-loading="isLoading"
    @item-clicked="onItemClicked"
  />
</template>

<script setup lang="ts" generic="T extends BreadcrumbItem">
import { Breadcrumb as UiBreadcrumb, type BreadcrumbItem } from '@boqnet/ui';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { isCurrentCrumb } from '@/common/utils/breadcrumb-trail.utils';

const route = useRoute();

const props = withDefaults(
  defineProps<{
    breadcrumbItems: T[];
    /**
     * Renders the library's placeholder bars instead of the trail. A view whose crumb labels come
     * from a request sets this while that request is in flight, so the trail arrives at its full
     * width rather than growing a crumb a moment after the page paints.
     */
    isLoading?: boolean;
  }>(),
  { isLoading: false },
);

const emit = defineEmits<{
  'item-clicked': [item: T];
}>();

// Context: the default branch hides the trail below `sm`, so this is mobile visibility policy
// and not padding — which is why this wrapper survives the move onto @boqnet/ui rather than
// pushing its class out to twelve call sites, each free to get the breakpoint wrong (PLE-1406).
const containerClass = computed(() => {
  if (route.name === 'boq') {
    return 'flex pt-3 sm:pt-6';
  }
  if (route.name === 'orders') {
    return 'flex';
  }
  if (route.name === 'accounts-payable') {
    return 'flex';
  }
  return 'hidden sm:flex';
});

const items = computed<BreadcrumbItem[]>(() =>
  props.breadcrumbItems.map((item, index) => ({
    ...item,
    isCurrent: isCurrentCrumb(item, index, props.breadcrumbItems.length),
  })),
);

/**
 * Hands back the caller's own item, not the copy this wrapper built. Several call sites carry
 * fields the library knows nothing about — `PaymentRequestDetailsSidebar` switches on a `type`
 * discriminant — and the generic keeps those typed all the way through.
 */
const onItemClicked = (item: BreadcrumbItem) => {
  const original = props.breadcrumbItems.find((candidate) => candidate.index === item.index);

  if (original) {
    emit('item-clicked', original);
  }
};
</script>
